import { TrendingUpDown } from "lucide-react";
import { Area, AreaChart, YAxis } from "recharts";

import { MetricGlossaryLabel } from "src/components/MetricGlossaryLabel/MetricGlossaryLabel";
import { Badge } from "src/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "src/components/ui/card";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "src/components/ui/chart";
import { Skeleton } from "src/components/ui/skeleton";
import { cn } from "src/lib/utils";

export const KpiCard = ({
  title,
  subtitle,
  loading,
  value,
  format = (val) => val,
  secondary,
  secondaryFormat = (val) => val,
  diff,
  diffFormat = (val) => val,
  diffClassName,
  chart,
  chartClassName,
}) => {
  const { data, config, dataKey } = chart || {};
  const hasValue = value !== null && value !== undefined;
  const hasSecondary = secondary !== null && secondary !== undefined;

  return (
    <Card
      className="@container/card flex flex-col justify-between gap-4"
      key={title}
    >
      <CardHeader className="relative space-y-2">
        <div className="text-card-foreground text-base">
          <MetricGlossaryLabel metric={title}>{title}</MetricGlossaryLabel>
        </div>
        <CardTitle
          className={cn(
            "mt-2 text-3xl font-semibold tabular-nums @[250px]/card:text-4xl",
            loading &&
              hasValue &&
              "text-muted-foreground animate-pulse blur-[1px]"
          )}
        >
          {loading && !hasValue ? (
            <Skeleton className="h-10 w-full" />
          ) : (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              {hasValue ? format(value) : "..."}
              {!!diff && (
                <Badge variant="outline" className={cn("h-6", diffClassName)}>
                  {diffFormat(diff)}
                </Badge>
              )}
            </div>
          )}
        </CardTitle>

        {hasSecondary && (
          <CardDescription
            className={cn(
              "-mt-3 flex items-center gap-1",
              loading && "animate-pulse blur-[1px]"
            )}
          >
            <TrendingUpDown className="size-3" />
            <span className="tabular-nums">{secondaryFormat(secondary)}</span>
          </CardDescription>
        )}

        {subtitle && <CardDescription>{subtitle}</CardDescription>}
      </CardHeader>

      {chart && (
        <CardContent className={cn("h-full", !loading && "sm:-mx-6 -mx-4")}>
          {loading && (
            <Skeleton className={cn("h-28 w-full px-6", chartClassName)} />
          )}
          {!loading && (
            <ChartContainer
              config={config}
              className={cn("h-28 w-full", chartClassName)}
            >
              <AreaChart
                accessibilityLayer
                data={data}
                margin={{
                  top: 10,
                }}
              >
                {!loading && (
                  <ChartTooltip
                    cursor={false}
                    content={
                      <ChartTooltipContent
                        hideLabel
                        hideIndicator
                        valueFormatter={(v) => format(v)}
                        formatter={({ item }) => {
                          const label = item?.payload?.[dataKey];
                          const value = item?.value;

                          return (
                            <div className="flex w-full justify-between gap-2">
                              <span className="text-muted-foreground">
                                {label}
                              </span>
                              <span className="ml-1 font-mono font-medium tabular-nums">
                                {format(value)}
                              </span>
                            </div>
                          );
                        }}
                      />
                    }
                  />
                )}

                <defs>
                  {Object.entries(config).map(([key, config]) => (
                    <linearGradient
                      id={`${key + config.label?.replace(/[^a-zA-Z0-9]/g, "_")}-fade`}
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                      key={key}
                    >
                      <stop
                        offset="5%"
                        stopColor={config.color}
                        stopOpacity={0.8}
                      />
                      <stop
                        offset="95%"
                        stopColor={config.color}
                        stopOpacity={0}
                      />
                    </linearGradient>
                  ))}
                </defs>

                <YAxis
                  hide
                  padding={{
                    bottom: 2,
                  }}
                />

                {Object.entries(config).map(([key, config]) => {
                  return (
                    <Area
                      key={key}
                      dataKey={key}
                      type="monotone"
                      fill={`url(#${key + config.label?.replace(/[^a-zA-Z0-9]/g, "_")}-fade)`}
                      fillOpacity={0.4}
                      strokeWidth={3}
                      stroke={config.color}
                      stackId="a"
                    />
                  );
                })}
              </AreaChart>
            </ChartContainer>
          )}
        </CardContent>
      )}
    </Card>
  );
};
